import React, { createRef, useMemo, useState } from "react";
import { Control, FieldError } from "react-hook-form";

import { getSortedCountries } from "shared/utils";
import {
  CountryValue,
  CountryOption,
  FieldWrapper,
  InputFieldProps,
  SelectDropdown,
  SelectPlaceholder,
} from "shared/components";
import { IAutocomplete } from "shared/types";
import { Globe } from "shared/assets/icons";
import { copy } from "shared/assets/copy";

import { ISelectOption } from "../select/types";
import { InputFieldWrapper } from "../styles";
import { StyledPhoneField, StyledPhoneSelect, StyledPhoneNumber } from "./styles";

interface PhoneFieldProps extends InputFieldProps {
  control: Control;
}

interface PhoneValue {
  number?: string;
  code?: string;
}

interface PhoneError {
  number?: FieldError;
  code?: FieldError;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  registerRef,
  name,
  placeholder,
  value,
  required,
  disabled,
  readOnly,
  onChange,
  control,
  error,
  maxLength,
  ...rest
}) => {
  const { errors } = copy;
  const countries = getSortedCountries();

  const countryOptions = countries.map(
    ({ code, name, callingCode, flag }): ISelectOption => ({
      value: code,
      label: name,
      prefix: `+${callingCode}`,
      iconComponent: flag,
      searchableLabel: `+${callingCode} ${name}`,
    })
  );

  const selectedValue = useMemo(
    () => countryOptions.find(option => option.value === (value as PhoneValue)?.code),
    [value, countryOptions]
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const inputRef = createRef<HTMLSpanElement>();

  const handleClose = () => {
    setIsOpen(false);
  };

  const focusNumberField = () => {
    inputRef.current?.querySelector("input")?.focus();
  };

  return (
    <FieldWrapper
      name={name}
      required={required}
      error={(error as PhoneError)?.code || (error as PhoneError)?.number}
      {...rest}
    >
      <InputFieldWrapper isPadded error={!!error} isDisabled={disabled || readOnly}>
        <StyledPhoneField>
          <StyledPhoneSelect
            type="button"
            isOpen={isOpen}
            onClick={toggleOpen}
            onFocus={handleOpen}
          >
            {selectedValue ? (
              <CountryValue value={selectedValue} isOpen={isOpen} />
            ) : (
              <SelectPlaceholder icon={Globe} label="+..." isOpen={isOpen} />
            )}
          </StyledPhoneSelect>
          <StyledPhoneNumber ref={inputRef}>
            <input
              id={name}
              name={`${name}.number`}
              type="text"
              placeholder={placeholder}
              ref={registerRef}
              onChange={onChange}
              disabled={disabled}
              readOnly={readOnly}
              autoComplete={IAutocomplete.telNational}
              aria-required={required}
              maxLength={maxLength}
            />
          </StyledPhoneNumber>
        </StyledPhoneField>
      </InputFieldWrapper>
      <SelectDropdown
        name={`${name}.code`}
        selectedValue={selectedValue}
        handleClose={handleClose}
        handleBlur={focusNumberField}
        control={control}
        options={countryOptions}
        Option={CountryOption}
        isOpen={isOpen}
        isSearchable
        autocomplete={IAutocomplete.country}
        validation={{
          required: (value as PhoneError)?.number ? errors.callingCode.required : false,
        }}
        isFocused
      />
    </FieldWrapper>
  );
};
